import { api } from './api';

// urlBase64ToUint8Array — VAPID public key comes as base64url; the Push API
// wants a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

// Register the service worker once (safe to call repeatedly)
let swRegistration = null;
export async function ensureServiceWorker() {
  if (!pushSupported()) return null;
  if (swRegistration) return swRegistration;
  swRegistration = await navigator.serviceWorker.register('/sw.js');
  return swRegistration;
}

// Current subscription state for this device: 'unsupported' | 'denied' | 'off' | 'on'
export async function getPushState() {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  const reg = await ensureServiceWorker();
  if (!reg) return 'unsupported';
  const sub = await reg.pushManager.getSubscription();
  return sub ? 'on' : 'off';
}

// Ask permission + subscribe this device
export async function enablePush() {
  if (!pushSupported()) throw new Error('Push not supported on this device');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notification permission was not granted');

  const { key, enabled } = await api.pushVapidKey();
  if (!enabled || !key) throw new Error('Push is not configured on the server');

  const reg = await ensureServiceWorker();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  }
  await api.pushSubscribe(sub);
  return 'on';
}

// Unsubscribe this device
export async function disablePush() {
  const reg = await ensureServiceWorker();
  if (!reg) return 'off';
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.pushUnsubscribe(sub.endpoint).catch(() => {});
    await sub.unsubscribe().catch(() => {});
  }
  return 'off';
}

export async function sendTestPush() {
  return api.pushTest();
}
